import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type ImportKind = "customers" | "products";

interface RowReport {
  row: number;
  status: "ok" | "skipped" | "failed";
  message?: string;
  key?: string;
}

interface Summary {
  total: number;
  success: number;
  skipped: number;
  failed: number;
  rows: RowReport[];
}

const KIND_LABEL: Record<ImportKind, string> = {
  customers: "Khách hàng",
  products: "Sản phẩm + tồn kho",
};

const KIND_HINT: Record<ImportKind, string> = {
  customers:
    "Upload file xlsx export từ nhanh.vn — Khách hàng. Trùng SĐT sẽ được cập nhật, không tạo bản sao.",
  products:
    "Upload file xlsx export từ nhanh.vn — Sản phẩm. Tự tạo danh mục, ghi đè tồn kho ban đầu vào kho mặc định.",
};

export function DataImportPage() {
  const [kind, setKind] = useState<ImportKind>("customers");
  const [file, setFile] = useState<File | null>(null);

  // Track which kind produced the most recent summary so the report card stays
  // accurate when the user toggles tabs without re-uploading.
  const [lastResult, setLastResult] = useState<{ kind: ImportKind; summary: Summary } | null>(null);

  const upload = useMutation({
    mutationFn: async (vars: { kind: ImportKind; file: File }) => {
      const fd = new FormData();
      fd.append("file", vars.file);
      const res = await apiClient.post(`/v1/import/${vars.kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // xlsx parse + bulk insert can take a while for large files (8k+ rows).
        // Products import does per-row upserts and can run 10+ minutes on
        // big catalogs — give it 30 min before axios aborts.
        timeout: 30 * 60 * 1000,
      });
      return res.data.data as Summary;
    },
    onSuccess: (summary, vars) => setLastResult({ kind: vars.kind, summary }),
  });

  function handleSubmit() {
    if (!file) return;
    upload.mutate({ kind, file });
  }

  const errorMsg = upload.error ? translateApiError(upload.error) : undefined;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Import dữ liệu</h1>
        <p className="text-sm text-muted-foreground">
          Import từ file xlsx export nhanh.vn. Chỉ admin sử dụng được.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex gap-2">
            {(["customers", "products"] as const).map((k) => (
              <Button
                key={k}
                size="sm"
                variant={kind === k ? "default" : "outline"}
                onClick={() => {
                  setKind(k);
                  setFile(null);
                }}
              >
                {KIND_LABEL[k]}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{KIND_HINT[kind]}</p>

          <input
            // Remount on tab switch so the displayed filename resets and a
            // re-pick of the same file still fires onChange.
            key={kind}
            type="file"
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
          />

          {file && (
            <p className="text-sm">
              Đã chọn: <span className="font-medium">{file.name}</span> (
              {(file.size / 1024 / 1024).toFixed(1)} MB)
            </p>
          )}

          {errorMsg && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </p>
          )}

          <Button onClick={handleSubmit} disabled={!file || upload.isPending}>
            {upload.isPending ? "Đang import…" : "Import"}
          </Button>
        </CardContent>
      </Card>

      {lastResult && <ResultCard kind={lastResult.kind} summary={lastResult.summary} />}
    </div>
  );
}

function ResultCard({ kind, summary }: { kind: ImportKind; summary: Summary }) {
  const issues = summary.rows.filter((r) => r.status !== "ok");
  return (
    <Card>
      <CardHeader>
        <h2 className="font-medium">Kết quả import {KIND_LABEL[kind]}</h2>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <Stat label="Tổng" value={summary.total} />
          <Stat label="Thành công" value={summary.success} tone="success" />
          <Stat label="Bỏ qua" value={summary.skipped} tone="warn" />
          <Stat label="Lỗi" value={summary.failed} tone="error" />
        </div>

        {issues.length > 0 && (
          <details>
            <summary className="cursor-pointer text-sm font-medium">
              Xem {issues.length} dòng có vấn đề
            </summary>
            <div className="mt-2 max-h-80 overflow-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-2 py-1">Dòng</th>
                    <th className="px-2 py-1">Trạng thái</th>
                    <th className="px-2 py-1">Khoá</th>
                    <th className="px-2 py-1">Lý do</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((r) => (
                    <tr key={r.row} className="border-t">
                      <td className="px-2 py-1">{r.row}</td>
                      <td className="px-2 py-1">{r.status}</td>
                      <td className="px-2 py-1 font-mono">{r.key ?? "—"}</td>
                      <td className="px-2 py-1">{r.message ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "success" | "warn" | "error";
}) {
  const colour =
    tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "error"
          ? "text-destructive"
          : "";
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-semibold ${colour}`}>{value}</p>
    </div>
  );
}
